import React from 'react';
import styled from 'styled-components';

import { v4 as uuidv4 } from 'uuid';
import { Alert, Box, Divider, Stack, Switch } from '@mui/material';
import { Command, tabActions } from '@store/slices';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@common/Button';
import { Label } from '@common/Label';
import { Select } from '@common/Select';
import { Option } from '@common/BucketInput/BucketField';
import { useNodeData } from '@common/hooks/useNodeData';
import { Expression } from '@common/Expression';
import { NodeAIArgument, NodeField } from '@components/Flow/Nodes';
import { State } from '@store/index';
import { Tooltip } from '@common/Tooltip';
import { InfoOutlined } from '@mui/icons-material';
import { useTheme } from '../../../../styles';
import { useParams } from 'react-router-dom';
import { SubText } from '@common/SubText/SubText';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;

  & .MuiInputAdornment-root {
    margin-right: 10px;
  }
`;

const ArgumentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 6px;
`;

const Notice = styled.p<{ error?: boolean }>`
  font-size: 12px;
  color: ${({ theme, error }) => (error ? theme.telnyx.red : theme.text.subtext)};
  padding-left: 12px;
`;

interface Props {
  command: Command;
  field: NodeField;
}

export const ToolArguments: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { state } = command;
  const { id, label, description } = field;
  const { getOptions, dataEdges } = useNodeData(command.node.id);
  const { workflow_id } = useParams();

  const nodeErrors = useSelector((state: State) => state.tabs.active.nodeErrors);

  const value = state[id] as NodeAIArgument[];
  const options = getOptions(command.node.id);

  const error = nodeErrors?.find(
    (nodeError) =>
      nodeError.id === command.node.id &&
      nodeError?.workflowId === workflow_id &&
      nodeError?.fields?.find((nodeField) => nodeField.id === field.id)
  );
  const errorField = error ? error?.fields?.find((nodeField) => nodeField.id === field.id) : undefined;
  const errorFieldMessage = errorField ? errorField.message : undefined;

  const addArgument = () => {
    const input = {
      nodeId: command.node.id,
      fieldId: id,
      value: {
        id: uuidv4(),
        name: '',
        description: '',
        enumerator: '',
        type: 'string' as const,
      },
    };

    dispatch(tabActions.nodeInputArrayPush(input));
    dispatch(tabActions.setSaved(false));
  };

  const updateField = (nestedFieldId: string, fieldName: string, value: string) => {
    if (fieldName === 'enumerator') {
      const enums = value.split(',');
      const enumFormatted = [];

      for (const item of enums) {
        enumFormatted.push(item.replace(/\n/g, ' ').trim());
      }

      value = enumFormatted.join(',\n');
    }

    const input = {
      nodeId: command.node.id,
      fieldId: id,
      nestedFieldId,
      fieldName,
      value,
    };

    if (fieldName === 'name') dispatch(tabActions.removeNodeErrors(command.node.id));
    dispatch(tabActions.nodeInputArrayUpdate(input));
    dispatch(tabActions.setSaved(false));
  };

  const deleteField = (nestedFieldId: string) => {
    dispatch(
      tabActions.nodeInputArrayFilter({
        nodeId: command.node.id,
        fieldId: id,
        nestedFieldId,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const searchTypeOptions: Option[] = [
    { label: 'string', value: 'string' },
    { label: 'boolean', value: 'boolean' },
    { label: 'enum', value: 'enum' },
    { label: 'number', value: 'Number' },
  ];

  if (!field || !command) return null;

  return (
    <Container>
      {value?.length > 0 && (
        <Stack spacing={2} divider={<Divider flexItem />}>
          {value?.map((params) => {
            const { id, name, description: _description, type, enumerator } = params;
            const required = typeof params.required === 'undefined' ? true : params?.required === 'true';

            return (
              <ArgumentBlock key={id}>
                <Stack direction='row' justifyContent='space-between'>
                  <Label title={'Argument'} tooltip={description} onDelete={() => deleteField(id)} />
                  {command.config.command_id === 'call.ai_gather' && (
                    <Stack alignItems='center' spacing={0.75} direction='row'>
                      <Tooltip
                        sx={{ maxWidth: '300px' }}
                        placement='top'
                        arrow
                        title='This determines if the AI is required to output this argument or not. If not required, the AI will sometimes fill in a dummy value for this argument.'
                      >
                        <InfoOutlined sx={{ fontSize: '16px', color: theme.text.subtext }} />
                      </Tooltip>
                      <SubText>Required</SubText>
                      <Switch
                        size='small'
                        checked={required}
                        onChange={(e) => updateField(id, 'required', e?.target?.checked?.toString())}
                      />
                    </Stack>
                  )}
                </Stack>
                <Stack display='grid' gridTemplateColumns='6fr 4fr' gap='8px'>
                  <Expression
                    key={`${id}name`}
                    value={name}
                    dataEdges={dataEdges}
                    title={label}
                    description={'This is the name of the argument as it will be described to the LLM.'}
                    setValue={(value: string) => updateField(id, 'name', value)}
                    options={options}
                    placeholder={'Name'}
                  />
                  <Select
                    key={`${id}_select`}
                    value={type}
                    setValue={(value) => updateField(id, 'type', value)}
                    options={searchTypeOptions}
                    placeholder='Select an index search type'
                  />
                </Stack>
                <Expression
                  key={`${id}_description`}
                  value={_description}
                  dataEdges={dataEdges}
                  title={label}
                  description={
                    'This is the description that the LLM will read to determine how to generate this argument.'
                  }
                  setValue={(value: string) => updateField(id, 'description', value)}
                  options={options}
                  placeholder={'Description'}
                />
                {type === 'enum' && (
                  <>
                    <Expression
                      key={`${id})enum`}
                      value={enumerator}
                      dataEdges={dataEdges}
                      title={label}
                      description={
                        'This is an enumerator type. You should separate the values using commas.\nFor example: "dog, cat, sheep".'
                      }
                      setValue={(value: string) => updateField(id, 'enumerator', value)}
                      options={options}
                      placeholder={'Enums'}
                    />
                    <Notice error={enumerator?.length > 0 && !enumerator?.includes(',')}>
                      {enumerator?.length > 0 && !enumerator?.includes(',')
                        ? 'An enum type must have at least two values separated by a comma.'
                        : 'Enumerators are separated using a comma (,)'}
                    </Notice>
                  </>
                )}
              </ArgumentBlock>
            );
          })}
        </Stack>
      )}
      {errorFieldMessage && (
        <Alert severity='error' sx={{ mt: value?.length > 0 ? 1 : 0 }}>
          {errorFieldMessage}
        </Alert>
      )}
      <Box sx={{ mt: value?.length > 0 ? 1 : 0 }}>
        <Button customColor='black' fullWidth onClick={addArgument}>
          Add Argument
        </Button>
      </Box>
    </Container>
  );
};
